// Create / edit an attribute value, scoped to a type. Value is immutable once
// created — it's the stable identifier products reference. On create it is
// derived automatically from the label (slugifyAttributeValue) and only shown
// as a preview; in edit mode it renders disabled, mirroring type-form-modal's
// Key field.

import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  useToast,
} from '@elkaso-app/web-design';
import { AttributeValueAdmin, useCreateAttributeValueApi, useUpdateAttributeValueApi } from 'apis/product-attributes';
import { useEffect, useState } from 'react';
import { slugifyAttributeValue } from './slugify-attribute-value';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  typeId: number;
  value?: AttributeValueAdmin; // present -> edit, absent -> create
}

interface FormState {
  value: string;
  label: string;
  synonyms: string; // comma-separated, parsed on submit
  sortOrder: string;
  isActive: boolean;
}

const emptyForm = (): FormState => ({
  value: '',
  label: '',
  synonyms: '',
  sortOrder: '',
  isActive: true,
});

const fromValue = (v: AttributeValueAdmin): FormState => ({
  value: v.value,
  label: v.label,
  synonyms: (v.synonyms ?? []).join(', '),
  sortOrder: String(v.sortOrder),
  isActive: v.isActive,
});

export const ValueFormModal = ({ isOpen, onClose, typeId, value }: Props) => {
  const toast = useToast();
  const isEdit = !!value;
  const [form, setForm] = useState<FormState>(emptyForm());

  useEffect(() => {
    if (isOpen) setForm(value ? fromValue(value) : emptyForm());
  }, [isOpen, value]);

  const set = <K extends keyof FormState>(key: K, val: FormState[K]) => setForm((f) => ({ ...f, [key]: val }));

  const onDone = () => {
    toast({ title: `Attribute value ${isEdit ? 'updated' : 'created'}`, status: 'success' });
    onClose();
  };
  const onError = (error: any) => {
    // hooks toast server messages; this is the network-error fallback
    if (!error?.response?.data?.message) {
      toast({ title: `Failed to ${isEdit ? 'update' : 'create'} attribute value`, status: 'error' });
    }
  };

  const createApi = useCreateAttributeValueApi({ onSuccess: onDone, onError });
  const updateApi = useUpdateAttributeValueApi({ onSuccess: onDone, onError });
  const isSaving = createApi.isLoading || updateApi.isLoading;

  // Create-mode identifier, derived live from the label — never typed by hand.
  const derivedValue = slugifyAttributeValue(form.label);

  const submit = () => {
    if (!form.label.trim()) {
      toast({ title: 'Label is required', status: 'warning' });
      return;
    }
    if (!isEdit && !derivedValue) {
      toast({ title: 'Label must contain at least one letter or digit', status: 'warning' });
      return;
    }
    const sortOrderNum = form.sortOrder.trim() ? Number(form.sortOrder) : undefined;
    if (sortOrderNum !== undefined && !Number.isFinite(sortOrderNum)) {
      toast({ title: 'Sort order must be a number', status: 'warning' });
      return;
    }
    const synonyms = form.synonyms
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (isEdit && value) {
      updateApi.mutate({
        id: value.id,
        data: {
          label: form.label.trim(),
          synonyms,
          sortOrder: sortOrderNum,
          isActive: form.isActive,
        },
      });
    } else {
      createApi.mutate({
        typeId,
        data: {
          value: derivedValue,
          label: form.label.trim(),
          synonyms,
          sortOrder: sortOrderNum,
          isActive: form.isActive,
        },
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='md'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit Attribute Value' : 'New Attribute Value'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired mb='3'>
            <FormLabel fontSize='sm'>Label</FormLabel>
            <Input
              value={form.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder='e.g. Organic'
              autoFocus={!isEdit}
            />
          </FormControl>
          <FormControl mb='3'>
            <FormLabel fontSize='sm'>Value</FormLabel>
            <Input value={isEdit ? form.value : derivedValue} isDisabled placeholder='auto-generated from the label' />
            <FormHelperText>
              {isEdit ? 'lowercase slug, immutable' : 'auto-generated from the label, immutable after creation'}
            </FormHelperText>
          </FormControl>
          <FormControl mb='3'>
            <FormLabel fontSize='sm'>Synonyms</FormLabel>
            <Input
              value={form.synonyms}
              onChange={(e) => set('synonyms', e.target.value)}
              placeholder='comma-separated, e.g. bio, eco'
            />
            <FormHelperText>Comma-separated list</FormHelperText>
          </FormControl>
          <FormControl mb='3'>
            <FormLabel fontSize='sm'>Sort order</FormLabel>
            <Input type='number' value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
          </FormControl>
          <Flex align='center' gap='2'>
            <Switch isChecked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} colorScheme='green' />
            <Text fontSize='sm'>Active</Text>
          </Flex>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme='red' onClick={submit} isLoading={isSaving}>
            {isEdit ? 'Save Changes' : 'Create Value'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
